import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { 
  RefreshCw, Loader2, CheckCircle, AlertTriangle, XCircle,
  ArrowUpRight, ArrowDownRight, Scale, TrendingUp, TrendingDown,
  FileText, Calculator, ChevronDown, ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ReconciliationDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  });
  const [showAll, setShowAll] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/cash-flow/reconciliation-dashboard?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      setData(response.data);
    } catch (error) {
      toast.error(`Failed to fetch reconciliation data: ${getErrorMessage(error)}`);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'matched':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'cf_higher':
        return <ArrowUpRight className="w-4 h-4 text-amber-500" />;
      case 'el_higher':
        return <ArrowDownRight className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'matched':
        return <Badge className="bg-green-100 text-green-800">Matched</Badge>;
      case 'cf_higher':
        return <Badge className="bg-amber-100 text-amber-800">CF Higher</Badge>;
      case 'el_higher':
        return <Badge className="bg-red-100 text-red-800">EL Higher</Badge>;
      default:
        return null;
    }
  };

  const displayCategories = showAll ? data?.by_category : data?.by_category?.slice(0, 10);

  return (
    <div className="space-y-6 p-6" data-testid="reconciliation-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reconciliation Dashboard</h1>
          <p className="text-gray-500">Compare Cash Flow and Expense Ledger</p>
        </div>
        
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-36"
              data-testid="recon-start-date"
            />
          </div>
          <div>
            <Label className="text-xs">End Date</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-36"
              data-testid="recon-end-date"
            />
          </div>
          <Button onClick={fetchData} disabled={loading} data-testid="recon-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      ) : data ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Cash Flow Total */}
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-blue-600 mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-sm font-medium">Cash Flow Total</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(data.summary.cash_flow_total)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Restaurant: {formatCurrency(data.summary.cash_flow_restaurant)}
                </p>
              </CardContent>
            </Card>

            {/* Expense Ledger Total */}
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-purple-600 mb-1">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm font-medium">Expense Ledger Total</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(data.summary.expense_ledger_total)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  From day closings + bank txns
                </p>
              </CardContent>
            </Card>

            {/* Difference */}
            <Card className={`border-l-4 ${Math.abs(data.summary.total_difference) < 100 ? 'border-l-green-500' : 'border-l-red-500'}`}>
              <CardContent className="p-4">
                <div className={`flex items-center gap-2 mb-1 ${Math.abs(data.summary.total_difference) < 100 ? 'text-green-600' : 'text-red-600'}`}>
                  <Scale className="w-4 h-4" />
                  <span className="text-sm font-medium">Difference</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(Math.abs(data.summary.total_difference))}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {data.summary.total_difference > 0 ? 'CF higher' : data.summary.total_difference < 0 ? 'EL higher' : 'Balanced'}
                </p>
              </CardContent>
            </Card>

            {/* Status */}
            <Card className={`border-l-4 ${data.summary.reconciliation_status === 'balanced' ? 'border-l-green-500 bg-green-50' : 'border-l-amber-500 bg-amber-50'}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  {data.summary.reconciliation_status === 'balanced' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  )}
                  <span className={`text-sm font-medium ${data.summary.reconciliation_status === 'balanced' ? 'text-green-600' : 'text-amber-600'}`}>
                    {data.summary.reconciliation_status === 'balanced' ? 'Balanced' : 'Discrepancy'}
                  </span>
                </div>
                <p className="text-lg font-semibold">
                  {data.summary.categories_matched} / {data.summary.categories_matched + data.summary.categories_mismatched} matched
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {data.summary.categories_mismatched} categories with difference
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Uncaptured Info */}
          {data.summary.cash_flow_uncaptured > 0 && (
            <Card className="border-amber-300 bg-amber-50/50">
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  <span className="font-medium text-amber-800">
                    Cash Flow has {formatCurrency(data.summary.cash_flow_uncaptured)} in Uncaptured Expenses
                  </span>
                </div>
                <p className="text-sm text-amber-700 mt-1">
                  These are bank debits not categorized into specific sections. Use Category Manager to map them.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Category Comparison Table */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Category-wise Comparison
                </CardTitle>
                <Badge variant="outline">
                  {data.discrepancies?.length || 0} discrepancies
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium">Category</th>
                      <th className="text-right p-3 font-medium">Cash Flow</th>
                      <th className="text-right p-3 font-medium">Expense Ledger</th>
                      <th className="text-right p-3 font-medium">Difference</th>
                      <th className="text-center p-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayCategories?.map((cat, idx) => (
                      <tr 
                        key={idx} 
                        className={`border-b hover:bg-gray-50 ${cat.status !== 'matched' ? 'bg-amber-50/30' : ''}`}
                      >
                        <td className="p-3 font-medium flex items-center gap-2">
                          {getStatusIcon(cat.status)}
                          {cat.category}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {formatCurrency(cat.cash_flow)}
                        </td>
                        <td className="p-3 text-right font-mono">
                          {formatCurrency(cat.expense_ledger)}
                        </td>
                        <td className={`p-3 text-right font-mono font-medium ${
                          cat.difference > 0 ? 'text-amber-600' : 
                          cat.difference < 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {cat.difference !== 0 && (cat.difference > 0 ? '+' : '')}
                          {formatCurrency(cat.difference)}
                        </td>
                        <td className="p-3 text-center">
                          {getStatusBadge(cat.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold">
                      <td className="p-3">TOTAL</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(data.summary.cash_flow_total)}</td>
                      <td className="p-3 text-right font-mono">{formatCurrency(data.summary.expense_ledger_total)}</td>
                      <td className={`p-3 text-right font-mono ${
                        data.summary.total_difference > 0 ? 'text-amber-600' : 
                        data.summary.total_difference < 0 ? 'text-red-600' : 'text-green-600'
                      }`}>
                        {data.summary.total_difference !== 0 && (data.summary.total_difference > 0 ? '+' : '')}
                        {formatCurrency(data.summary.total_difference)}
                      </td>
                      <td className="p-3 text-center">
                        {Math.abs(data.summary.total_difference) < 100 ? (
                          <Badge className="bg-green-100 text-green-800">Balanced</Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-800">Discrepancy</Badge>
                        )}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              
              {data.by_category?.length > 10 && (
                <div className="mt-4 text-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAll(!showAll)}
                  >
                    {showAll ? (
                      <>
                        <ChevronUp className="w-4 h-4 mr-2" />
                        Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4 mr-2" />
                        Show All {data.by_category.length} Categories
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="bg-gray-50">
            <CardContent className="p-4">
              <h3 className="font-medium mb-2">Understanding the Difference</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <p className="font-medium text-gray-700">Cash Flow includes:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Day closing deductions</li>
                    <li>Bank transactions (all debits)</li>
                    <li>Specialized collections (bill_payments, salary_payments, liability_payments)</li>
                    <li>Uncaptured bank debits (gap filler)</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-gray-700">Common causes of discrepancy:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Bank transactions without [GROUP/CATEGORY] format</li>
                    <li>Different date attribution methods</li>
                    <li>Category naming variations</li>
                    <li>Deduplication logic differences</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
